/* kitsters-magic-hour-lp.js - Magic Hour for Teams interaction layer:
   scroll-progress bar, staggered scroll reveals, count-up stats, hero
   collage cursor parallax + rotating photo pool, hero antigravity particle
   canvas (2026-08-18), live year-calendar
   highlight, parallax stickers, sticky CTA. Only transform/opacity/box-shadow are animated (perf budget,
   CLAUDE.md §8 / design-system §8), and prefers-reduced-motion is honoured
   throughout.
   (Removed: the scroll-scrubbed step timeline, 2026-08-12 with the horizontal
   three-step rebuild; the "In the room" photo tilt, 2026-08-13 with that
   section; the hero solar-system tap toggle + orbit kit-label cycling,
   2026-08-17 with the hero-collage rebuild - its [data-hero-stage],
   [data-kit-label] and [data-kit-options] hooks all left the Liquid in the
   same commit, §10.54. Each removal took its listeners with it - §10.84, a
   stale header comment convinces the next person.) */
(function () {
  'use strict';

  function init() {
    var root = document.querySelector('.kmh');
    if (!root || root.__wired) return;
    root.__wired = true;

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ── Scroll-progress bar ─────────────────────────────────────────── */
    var bar = root.querySelector('[data-progress-fill]');
    if (bar) {
      var updBar = function () {
        var h = document.documentElement.scrollHeight - window.innerHeight;
        var p = h > 0 ? window.scrollY / h : 0;
        bar.style.transform = 'scaleX(' + Math.min(1, Math.max(0, p)) + ')';
      };
      window.addEventListener('scroll', updBar, { passive: true });
      window.addEventListener('resize', updBar);
      updBar();
    }

    /* ── Staggered scroll reveals (data-delay = ms offset) ───────────── */
    var reveals = root.querySelectorAll('.kmh-reveal');
    if (reduce || !('IntersectionObserver' in window)) {
      reveals.forEach(function (el) { el.classList.add('is-in'); });
    } else {
      var rio = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var d = parseInt(el.getAttribute('data-delay') || '0', 10) || 0;
          setTimeout(function () { el.classList.add('is-in'); }, d);
          rio.unobserve(el);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
      reveals.forEach(function (el) { rio.observe(el); });
    }

    /* ── Count-up stats - parses the rendered text ("31,000+") so the
          theme-editor setting stays free-form ─────────────────────────── */
    var counters = root.querySelectorAll('[data-countup]');
    function animateCount(el) {
      var m = /^([^0-9]*)([0-9,]+(?:\.[0-9]+)?)([\s\S]*)$/.exec(el.textContent.trim());
      if (!m) return;
      var prefix = m[1];
      var target = parseFloat(m[2].replace(/,/g, ''));
      var suffix = m[3];
      var decimals = (m[2].indexOf('.') > -1) ? m[2].split('.')[1].length : 0;
      var grouped = m[2].indexOf(',') > -1;
      if (reduce || isNaN(target)) return;
      var start = null;
      var duration = 1200;
      function fmt(v) {
        if (decimals) return v.toFixed(decimals);
        var n = Math.round(v);
        /* en-US, not en-IN: Indian digit grouping is lakh-based, so 100000 renders
           as "1,00,000" instead of "100,000". Identical below six figures, which is
           why it has never shown on the current counters — and wrong the day one of
           them passes 100,000. */
        return grouped ? n.toLocaleString('en-US') : String(n);
      }
      function step(ts) {
        if (!start) start = ts;
        var progress = Math.min(1, (ts - start) / duration);
        var eased = 1 - Math.pow(1 - progress, 3);
        el.textContent = prefix + fmt(target * eased) + suffix;
        if (progress < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }
    if (counters.length && 'IntersectionObserver' in window) {
      var cio = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { animateCount(entry.target); cio.unobserve(entry.target); }
        });
      }, { threshold: 0.6 });
      counters.forEach(function (el) { cio.observe(el); });
    }

    /* ── Hero collage - cursor parallax (2026-08-17) ──────────────────
       The grit.pictures behaviour: every collage card drifts toward (or
       away from - negative depth) the cursor, each at its own factor, so
       the pile reads as layers in space. Cursor position is lerped toward
       its target inside one rAF loop, so the follow is eased and a single
       frame of main-thread lag reads as intent, not jank (§10.56 - this is
       a POINTER effect, not a scroll-linked one; scroll drift stays with
       the CSS/[data-sticker] path). Writes go to element.style.transform
       on the dedicated .kmh__collage-par layer - never an inherited custom
       property (§10.56: those restyle the whole subtree per frame), and
       never the outer card, whose transform CSS owns (tilt + scroll drift).
       Gates: reduced-motion, and a real hover+fine pointer - on touch the
       collage keeps its CSS entrance/bob and simply doesn't track.
       All names col* - §10.59: a second bare `ticking` in this init() would
       silently share the sticker handler's binding. */
    var collage = root.querySelector('[data-hero-collage]');
    if (collage && !reduce && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      var colParts = [].slice.call(collage.querySelectorAll('[data-collage-par]'));
      var colHero = collage.closest('.kmh__hero') || collage;
      /* ── Hero cursor dot scatter (owner 2026-08-17, third pass: "Cursor
         effect should be like the screenshot dot effect", antigravity.google
         reference) ── REPLACES the follower dot + lagging ring. The
         reference hero is a field of tiny scattered specks, so the effect is
         now a TRAIL: as the pointer moves, small dash-shaped dots spawn at
         the cursor, drift outward a few px and fade - CSS owns the whole
         particle animation (one keyframe run per spawn, GPU-composited);
         JS only stamps position + per-dot randoms and re-arms the class.
         A fixed pool of nodes is recycled round-robin - never
         createElement per move - and a spawn needs >=14px of travel, so an
         idle cursor costs nothing. Colours are yellow / pink / white:
         the brand accents that read on the indigo band (indigo dots would
         vanish on it, §10.79's "know what failure looks like").
         The card parallax below is untouched: same rAF lerp, same listener,
         writes only to .kmh__collage-par (§10.56). */
      var colDots = colHero.querySelector('[data-hero-dotfield]');
      var colDotPool = [], colDotMax = 36, colDotI = 0;
      var colLastX = null, colLastY = null;
      var colDotColors = ['var(--color-yellow)', 'var(--color-pink)', 'rgba(255,255,255,.92)'];
      if (colDots) {
        for (var cdi = 0; cdi < colDotMax; cdi++) {
          colDotPool.push(colDots.appendChild(document.createElement('span')));
        }
      }
      var colSpawn = function (x, y) {
        var cd = colDotPool[colDotI];
        colDotI = (colDotI + 1) % colDotMax;
        var colAng = Math.random() * Math.PI * 2;
        var colDist = 16 + Math.random() * 30;
        cd.classList.remove('is-live');
        cd.style.left = (x + Math.random() * 14 - 7).toFixed(1) + 'px';
        cd.style.top = (y + Math.random() * 14 - 7).toFixed(1) + 'px';
        cd.style.setProperty('--dx', (Math.cos(colAng) * colDist).toFixed(1) + 'px');
        cd.style.setProperty('--dy', (Math.sin(colAng) * colDist).toFixed(1) + 'px');
        cd.style.setProperty('--a', (Math.random() * 160 - 80).toFixed(0) + 'deg');
        cd.style.setProperty('--w', (5 + Math.random() * 4).toFixed(1) + 'px');
        cd.style.setProperty('--c', colDotColors[(Math.random() * colDotColors.length) | 0]);
        /* Reflow re-arms the keyframe when the pool wraps onto a dot whose
           previous run already ended - remove/add alone would not restart. */
        void cd.offsetWidth;
        cd.classList.add('is-live');
      };
      if (colParts.length || colDotPool.length) {
        var colTX = 0, colTY = 0, colCX = 0, colCY = 0, colRaf = null;
        var colStep = function () {
          colCX += (colTX - colCX) * 0.08;
          colCY += (colTY - colCY) * 0.08;
          colParts.forEach(function (part) {
            var colDepth = parseFloat(part.getAttribute('data-collage-par')) || 0.03;
            part.style.transform = 'translate3d(' + (colCX * colDepth).toFixed(2) + 'px,' + (colCY * colDepth).toFixed(2) + 'px,0)';
          });
          /* Keep lerping until the parallax settles, then release the
             rAF so an idle hero costs zero frames. */
          if (Math.abs(colTX - colCX) + Math.abs(colTY - colCY) > 0.5) {
            colRaf = window.requestAnimationFrame(colStep);
          } else {
            colRaf = null;
          }
        };
        colHero.addEventListener('mousemove', function (e) {
          var colRect = colHero.getBoundingClientRect();
          colTX = e.clientX - colRect.left - colRect.width / 2;
          colTY = e.clientY - colRect.top - colRect.height / 2;
          if (colDotPool.length) {
            var colX = e.clientX - colRect.left;
            var colY = e.clientY - colRect.top;
            if (colLastX === null || Math.abs(colX - colLastX) + Math.abs(colY - colLastY) >= 14) {
              colLastX = colX; colLastY = colY;
              colSpawn(colX, colY);
              if (Math.random() < 0.5) colSpawn(colX, colY);
            }
          }
          if (!colRaf) colRaf = window.requestAnimationFrame(colStep);
        }, { passive: true });
        colHero.addEventListener('mouseleave', function () {
          colTX = 0; colTY = 0;
          if (!colRaf) colRaf = window.requestAnimationFrame(colStep);
        }, { passive: true });
      }
    }

    /* ── Hero antigravity particle canvas (owner 2026-08-18) ──────────
       The full antigravity.google hero effect, ADDED as its own layer -
       the dot-scatter trail above is untouched. A hero-covering <canvas>
       ([data-hero-antigrav], z 1 under the z-2 content) holds a field of
       monochrome white geometric specks - filled circles, outlined squares,
       outlined triangles - that drift weightlessly upward with a slight
       sinusoidal sway and wrap around every edge. Depth fakes parallax:
       one random depth value (0.25 back … 1 front) drives size, opacity
       AND drift speed together, so small particles are dim and slow and
       large ones are bright and fast. The cursor carries a repulsion
       radius; inside it a velocity push (vx/vy) scaled by proximity and
       depth shoves particles away, and a flat 0.95 friction per frame
       bleeds that velocity off so they glide to a stop and resume the
       drift - physics identical to the reference spec. Perf budget: one
       rAF loop that runs ONLY while the hero is on screen (IO) and the
       tab visible, DPR capped at 2, particle count derived from area and
       clamped, and the whole block is skipped under reduced-motion so the
       canvas stays blank. Mouse wiring is gated on hover+fine pointer
       (touch keeps the ambient drift only). Listener lives on the HERO,
       not the canvas - the canvas is pointer-events:none, so nothing
       above it changes behaviour. All names ag* (§10.59: a second bare
       `ticking`/`raf` in this init() would share an earlier binding). */
    var agHero = root.querySelector('.kmh__hero');
    var agCanvas = agHero ? agHero.querySelector('[data-hero-antigrav]') : null;
    if (agCanvas && !reduce && agCanvas.getContext) {
      var agCtx = agCanvas.getContext('2d');
      var agW = 0, agH = 0;
      var agParts = [];
      var agT = 0;
      var agRaf = null, agOnScreen = false;
      var agMouseX = -1e4, agMouseY = -1e4, agMouseIn = false;
      var AG_RADIUS = 150;      /* repulsion radius, CSS px */
      var AG_FORCE = 1.7;       /* max push per frame at the cursor, front layer */
      var AG_FRICTION = 0.95;   /* damping - the reference spec's number */

      var agSpawn = function (anywhere) {
        var depth = 0.25 + Math.random() * 0.75;
        return {
          x: Math.random() * agW,
          y: anywhere ? Math.random() * agH : agH + 8,
          depth: depth,
          size: 1.6 + depth * 4.6,             /* ~2px back … ~6px front */
          alpha: 0.14 + depth * 0.5,
          shape: (Math.random() * 3) | 0,      /* 0 circle, 1 square, 2 triangle */
          rot: Math.random() * Math.PI * 2,
          rotV: (Math.random() - 0.5) * 0.012,
          swayP: Math.random() * Math.PI * 2,
          swayF: 0.25 + Math.random() * 0.5,
          drift: 0.14 + depth * 0.42,          /* px/frame upward - front moves faster */
          vx: 0, vy: 0
        };
      };

      var agResize = function () {
        var agRect = agHero.getBoundingClientRect();
        var agDpr = Math.min(2, window.devicePixelRatio || 1);
        agW = agRect.width; agH = agRect.height;
        agCanvas.width = Math.round(agW * agDpr);
        agCanvas.height = Math.round(agH * agDpr);
        agCtx.setTransform(agDpr, 0, 0, agDpr, 0, 0);
        /* Density from area, clamped: a 320px phone hero gets ~26, a 1440
           desktop hero ~80 - enough to read as a field, never a snowstorm. */
        var agWant = Math.max(26, Math.min(90, Math.round((agW * agH) / 16000)));
        while (agParts.length < agWant) agParts.push(agSpawn(true));
        if (agParts.length > agWant) agParts.length = agWant;
      };

      var agDraw = function (p) {
        agCtx.globalAlpha = p.alpha;
        agCtx.beginPath();
        if (p.shape === 0) {
          agCtx.fillStyle = '#ffffff';
          agCtx.arc(p.x, p.y, p.size / 2, 0, Math.PI * 2);
          agCtx.fill();
        } else {
          agCtx.strokeStyle = '#ffffff';
          agCtx.lineWidth = 1;
          agCtx.save();
          agCtx.translate(p.x, p.y);
          agCtx.rotate(p.rot);
          var s = p.size;
          if (p.shape === 1) {
            agCtx.strokeRect(-s / 2, -s / 2, s, s);
          } else {
            agCtx.moveTo(0, -s * 0.62);
            agCtx.lineTo(s * 0.55, s * 0.38);
            agCtx.lineTo(-s * 0.55, s * 0.38);
            agCtx.closePath();
            agCtx.stroke();
          }
          agCtx.restore();
        }
      };

      var agStep = function () {
        agRaf = null;
        agT += 1 / 60;
        agCtx.clearRect(0, 0, agW, agH);
        for (var agI = 0; agI < agParts.length; agI++) {
          var p = agParts[agI];
          /* Repulsion: velocity push away from the cursor, strongest at the
             centre, scaled by depth so the foreground reacts harder. */
          if (agMouseIn) {
            var agDx = p.x - agMouseX, agDy = p.y - agMouseY;
            var agD2 = agDx * agDx + agDy * agDy;
            if (agD2 < AG_RADIUS * AG_RADIUS && agD2 > 0.01) {
              var agD = Math.sqrt(agD2);
              var agF = (1 - agD / AG_RADIUS) * AG_FORCE * p.depth;
              p.vx += (agDx / agD) * agF;
              p.vy += (agDy / agD) * agF;
            }
          }
          p.vx *= AG_FRICTION;
          p.vy *= AG_FRICTION;
          /* Base antigravity drift + slight horizontal sway. */
          p.x += p.vx + Math.sin(agT * p.swayF * Math.PI * 2 + p.swayP) * 0.22 * p.depth;
          p.y += p.vy - p.drift;
          p.rot += p.rotV;
          /* Seamless wrap: off the top -> re-enter from the bottom at a new
             x; off a side (or shoved out the bottom) -> opposite edge. */
          var agM = 12;
          if (p.y < -agM) { p.y = agH + agM; p.x = Math.random() * agW; }
          else if (p.y > agH + agM) { p.y = -agM; }
          if (p.x < -agM) { p.x = agW + agM; }
          else if (p.x > agW + agM) { p.x = -agM; }
          agDraw(p);
        }
        agCtx.globalAlpha = 1;
        if (agOnScreen && !document.hidden) agRaf = window.requestAnimationFrame(agStep);
      };
      var agKick = function () {
        if (!agRaf && agOnScreen && !document.hidden) agRaf = window.requestAnimationFrame(agStep);
      };

      agResize();
      if ('ResizeObserver' in window) {
        new ResizeObserver(agResize).observe(agHero);
      } else {
        window.addEventListener('resize', agResize);
      }
      /* Run only while the hero is actually on screen - an idle page costs
         zero frames. Fallback with no IO: always-on while the tab shows. */
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (agEntries) {
          agOnScreen = agEntries[0].isIntersecting;
          agKick();
        }, { threshold: 0.02 }).observe(agHero);
      } else {
        agOnScreen = true;
        agKick();
      }
      document.addEventListener('visibilitychange', agKick);

      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        agHero.addEventListener('mousemove', function (e) {
          var agRect = agHero.getBoundingClientRect();
          agMouseX = e.clientX - agRect.left;
          agMouseY = e.clientY - agRect.top;
          agMouseIn = true;
        }, { passive: true });
        agHero.addEventListener('mouseleave', function () {
          agMouseIn = false;
          agMouseX = -1e4; agMouseY = -1e4;
        }, { passive: true });
      }
    }

    /* ── Hero collage - rotating photo pool (2026-08-17, owner: "changing
       images should be have some animation") ─────────────────────────────
       One card at a time, round-robin - the same cadence contract as the
       old orbit-label cycler (all five flipping together reads as a slot
       machine). The pool is every wired hero_photo_* (JSON block, §4);
       cards start on photos 1-5 and rotate through the rest, skipping
       anything currently on screen so the collage never shows one photo
       twice. The next image is PRELOADED and the src swap happens at the
       FLIP animation's 50% keyframe (250ms of .5s), where the card is
       exactly edge-on (rotateY 90deg) - so a slow network shows the old
       photo longer instead of a blank frame, and the new photo rides the
       flip back out. Swapped imgs
       drop the width/height attrs' meaning? No - CSS aspect-ratio owns the
       box, so the swap cannot shift layout. Runs only while the collage is
       actually on screen (IntersectionObserver) and the tab is visible.
       Names colCycle* - §10.59. */
    if (collage && !reduce) {
      var colCycleImgs = [].slice.call(collage.querySelectorAll('[data-collage-img]'));
      var colCyclePoolEl = root.querySelector('[data-collage-pool]');
      var colCyclePool = [];
      if (colCyclePoolEl) {
        try { colCyclePool = JSON.parse(colCyclePoolEl.textContent) || []; } catch (err2) { colCyclePool = []; }
      }
      if (colCycleImgs.length && colCyclePool.length > colCycleImgs.length) {
        var colCycleCur = colCycleImgs.map(function (im) { return parseInt(im.getAttribute('data-pool-i'), 10) || 0; });
        /* One cursor per LANE - centre-people, people, kit. Lanes must never
           share a cursor: a lane that skips past everything it cannot use drags
           the shared cursor backwards and starves the others. Measured when the
           centre card and the small cards shared one, the retained photos
           appeared ZERO times in 4000 ticks. */
        var colCycleCursors = { big: colCycleImgs.length,
                                people: colCycleImgs.length,
                                kit: colCycleImgs.length };
        var colCycleSlot = 0;
        var colCycleVisible = true;
        /* ONE preload in flight at a time (2026-09-23, owner: "images loading is
           not optimised ... no initial blank spaces on image box"). The tick
           used to start a fresh `new Image()` every 1.7s whether or not the last
           one had arrived. On a slow phone a 50-100KB photo takes longer than
           that, so the preloads stacked up unfinished - measured on 1.6Mbps: a
           new request every ~1.7s from 14.8s on, none of them completing inside
           the 45s window - and every one of them was bandwidth taken from the
           gallery photos further down the page, which then sat as empty boxes
           for ~10s. With this guard a slow network simply rotates more slowly,
           which is the honest behaviour: the flip was only ever meant to happen
           once the next photo was ready. */
        var colCyclePending = false;

        if ('IntersectionObserver' in window) {
          new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) { colCycleVisible = entry.isIntersecting; });
          }, { threshold: 0 }).observe(collage);
        }

        var colCycleStep = function () {
          if (!colCycleVisible || document.hidden) return;
          var slot = colCycleSlot % colCycleImgs.length;
          colCycleSlot++;
          var img = colCycleImgs[slot];
          var card = img.closest('.kmh__collage-card');
          if (colCyclePending) return;
          if (!card || card.classList.contains('is-swapping')) return;
          var big = card.className.indexOf('kmh__collage-card--1') !== -1;
          /* Next pool index not currently on any card. The CENTRE card is
             additionally restricted to photos the pool marks centre-eligible
             (`c`, driven by the `hero_centre_slots` setting) - owner
             2026-09-12, "old images should not display on center card" - so a
             retained older photo still rotates through the four small cards
             but never the big one.
             Each LANE keeps a SEPARATE cursor, and that is load-bearing
             rather than tidiness. With one shared cursor the centre card's
             skip-past-the-keepers wraps the cursor back to the front of the
             pool every fifth tick, which starves slots 11-16 permanently -
             simulated over 4000 ticks, the retained photos appeared exactly
             zero times on ANY card. A cursor each: the centre walks the
             eligible photos, the small cards walk the whole pool, neither
             drags the other backwards.
             Terminates either way - each loop is bounded by the pool length
             and falls through to the `next === -1` guard below. */
          /* A card only ever swaps to a photo of ITS OWN category, which is what
             holds the owner's 2 people / 3 kit mix at every instant (owner
             2026-09-12). The category lives on the card in the Liquid
             (`data-cat`), so the markup is the single source of truth and this
             file cannot drift from it. */
          var wantPeople = card.getAttribute('data-cat') === 'people';
          var lane = big ? 'big' : (wantPeople ? 'people' : 'kit');
          var next = -1;
          var start = colCycleCursors[lane];
          for (var tries = 0; tries < colCyclePool.length; tries++) {
            var cand = (start + tries) % colCyclePool.length;
            if (colCycleCur.indexOf(cand) !== -1) continue;
            if (colCyclePool[cand].p !== wantPeople) continue;
            if (big && colCyclePool[cand].c === false) continue;
            next = cand;
            colCycleCursors[lane] = cand + 1;
            break;
          }
          if (next === -1) return;
          var entry = colCyclePool[next];
          var newSrc = big ? entry.l : entry.s;
          var newSet = big ? (entry.l + ' 640w, ' + entry.l2 + ' 960w')
                           : (entry.s + ' 520w, ' + entry.s2 + ' 780w');
          /* Preload the candidate the browser will ACTUALLY paint, not merely
             `newSrc`: the preloader gets the same sizes + srcset the real
             <img> resolves against, so `pre` fetches exactly what the swap
             will display.
             Measured 2026-09-12 (owner: "when the box flip, the image
             shouldn't change, and it change after some time"): at 1920 and
             2560 CSS px on a 2x display the <img> resolved to the 960w/780w
             candidate while only 640w/520w had been preloaded, so the swap
             started a FRESH request and the photo appeared ~790ms after the
             flip began - long after the .36s flip had ended. 390 and 1440
             resolved to the preloaded candidate and were never affected,
             which is why it read as "some boxes". Root CLAUDE.md §10.90c. */
          var pre = new Image();
          colCyclePending = true;
          pre.sizes = img.sizes;
          pre.srcset = newSet;
          pre.onload = function () {
            colCyclePending = false;
            card.classList.add('is-swapping');
            setTimeout(function () {
              /* srcset BEFORE src, same reason: src first lets the browser
                 commit to a candidate that the following srcset can replace. */
              img.srcset = newSet;
              img.src = newSrc;
              img.alt = entry.alt;
              colCycleCur[slot] = next;
            }, 180); /* the flip's edge-on point (50% of .36s in the CSS) */
            setTimeout(function () { card.classList.remove('is-swapping'); }, 400);
          };
          /* onerror: skip - the cursor already advanced, the next tick
             tries a different photo, and the card keeps its current one. It
             must still release the in-flight guard, or one broken file would
             stop the collage for the rest of the visit. */
          pre.onerror = function () { colCyclePending = false; };
          pre.src = newSrc;
        };
        /* 3200 -> 2600 ("little fast") -> 1700 (owner 2026-08-17, third
           pass: "make animation faster") - cadence AND the flip itself
           (.5s -> .36s, the two timeouts above rescaled with it). */
        /* Start only once the page's OWN images have loaded (window `load`):
           the rotation is decoration, and until then every preload it starts
           competes with the photos the shopper is actually waiting for. And
           not at all under Save-Data - the collage still shows its first five
           photos, it just stops downloading a sixteen-photo slideshow on a
           connection whose owner has asked for less. */
        var colCycleSaveData = !!(navigator.connection && navigator.connection.saveData);
        var colCycleStart = function () { setInterval(colCycleStep, 1700); };
        if (!colCycleSaveData) {
          if (document.readyState === 'complete') colCycleStart();
          else window.addEventListener('load', colCycleStart, { once: true });
        }
      }
    }

    /* ── (Scroll-scrubbed step timeline: DELETED 2026-08-12) ──────────
       The How-it-works band is a horizontal three-step flow now, so there is
       no vertical rail to fill, no per-step is-active reveal to scrub and no
       "Step 1 of 3" counter to drive. It read `[data-steps]`,
       `[data-steps-fill]`, `[data-step]` and `[data-steps-count]`; all four
       hooks left the Liquid in the same commit, and `[data-step-node]`,
       `[data-step-num]` and `[data-step-check]` went with them (they were
       already dead markup - zero JS references, §10.54).
       It also owned a window scroll + resize listener, which is why removing
       it is a perf win rather than a neutral change. Nothing below depended on
       `stepsSec`, `fill`, `steps`, `countEl`, `n` or `onScroll` - checked by
       diffing this file's hook inventory before and after, per §10.56. */

    /* ── Live calendar - highlight the current month + the next 3 ─────
       Class names here are the contract with `.kmh__cal-cell--*` in
       kitsters-magic-hour-lp.css; renamed 2026-08-13 with the strip → planner
       rebuild. The old version also scrolled the current month into view,
       because the strip was a 12-column horizontal scroller; the planner shows
       all twelve at once, so that block is gone rather than left as a no-op. */
    var cal = root.querySelector('[data-calendar]');
    if (cal) {
      var curMonth = new Date().getMonth();
      cal.querySelectorAll('[data-month]').forEach(function (cell) {
        var ahead = (parseInt(cell.getAttribute('data-month'), 10) - curMonth + 12) % 12;
        if (ahead > 3) return;
        cell.classList.add(ahead === 0 ? 'kmh__cal-cell--now' : 'kmh__cal-cell--soon');
        var flag = document.createElement('span');
        flag.className = 'kmh__cal-flag';
        flag.textContent = ahead === 0 ? 'This month' : (ahead === 1 ? 'Next' : 'In ' + ahead);
        cell.appendChild(flag);
      });
    }

    /* ── Floating stickers parallax ──────────────────────────────────── */
    var stickers = root.querySelectorAll('[data-sticker]');
    if (stickers.length && !reduce) {
      var ticking = false;
      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          var sy = window.scrollY;
          stickers.forEach(function (s) {
            var sp = parseFloat(s.getAttribute('data-sticker')) || 0.05;
            s.style.setProperty('--py', (-sy * sp) + 'px');
          });
          ticking = false;
        });
      }, { passive: true });
    }

    /* ── Pointer-tracked 3D parallax + glare for Tier-1 signature cards
          (curator worked-example card, pricing card, why-Kitsters band) ── */
    if (!reduce) {
      root.querySelectorAll('[data-hero3d]').forEach(function (card) {
        var glare = card.querySelector('[data-hero3d-glare]');
        var raf = null, tx = 0, ty = 0, cx = 0, cy = 0;
        var MAX = 7;
        var tick = function () {
          cx += (tx - cx) * 0.12;
          cy += (ty - cy) * 0.12;
          card.style.transform = 'perspective(1200px) rotateY(' + cx + 'deg) rotateX(' + (-cy) + 'deg) translateY(-4px)';
          if (Math.abs(tx - cx) > 0.05 || Math.abs(ty - cy) > 0.05) { raf = requestAnimationFrame(tick); } else { raf = null; }
        };
        card.addEventListener('mouseenter', function () { if (glare) glare.style.opacity = '1'; });
        card.addEventListener('mousemove', function (ev) {
          var r = card.getBoundingClientRect();
          var px = (ev.clientX - r.left) / r.width;
          var py = (ev.clientY - r.top) / r.height;
          tx = (px - 0.5) * 2 * MAX;
          ty = (py - 0.5) * 2 * MAX;
          if (glare) {
            glare.style.setProperty('--gx', (px * 100) + '%');
            glare.style.setProperty('--gy', (py * 100) + '%');
          }
          if (!raf) raf = requestAnimationFrame(tick);
        });
        card.addEventListener('mouseleave', function () {
          tx = 0; ty = 0;
          if (glare) glare.style.opacity = '0';
          if (!raf) raf = requestAnimationFrame(tick);
        });
      });

      /* (The "In the room" photo tilt handler was removed 2026-08-13 with
         that section - it was this page's only [data-tilt] element. The hook
         is still live on story / corporate / about, each of which binds it in
         its own JS, so nothing there is affected.) */
    }

    /* ── Sticky CTA (all widths since 2026-08-04) ─────────────────────────
       Visible only while BOTH ends of the page are off screen:
         - the hero CTA is out of view (nothing to duplicate yet), and
         - the closing band [data-sticky-end] is out of view.
       This ran mobile-only (max-width: 767px) until 2026-08-04 (owner
       request, same change as the Corporate page's sticky CTA - PR #872).
       The `mq` gate is gone; heroIn/pastEnd already work at every width, so
       nothing else about the visibility logic changed.
       The second condition is a bug fix (2026-08-03). The bar is 78px tall and
       position:fixed, so at the bottom of the page it sat permanently on top of
       the theme footer's Privacy / Terms / Refunds links - measured 22px of
       overlap at 320/375px and 26px at 430px, i.e. those links could not be
       tapped at all on a phone. The footer is outside this section, so it can't
       be given compensating padding from here; retiring the bar once the user
       reaches the closing band fixes the occlusion and is better UX anyway
       (the final CTA and the waitlist button are both right there).
       Tracked per-target so the two observers can't clobber each other. */
    var heroCta = root.querySelector('[data-hero-cta]');
    var stickyCta = root.querySelector('[data-sticky-cta]');
    var stickyEnd = root.querySelector('[data-sticky-end]');
    if (heroCta && stickyCta) {
      var heroIn = 'IntersectionObserver' in window; // in view until told otherwise
      /* NOT named `ticking`: the parallax-sticker scroll handler earlier in this
         same init() already declares `var ticking`, and `var` is FUNCTION-scoped,
         so a second `var ticking` here is the SAME binding, not a new one. That
         handler is registered first, so on every scroll event it set ticking =
         true before this one ran, and this handler then hit `if (ticking) return`
         and bailed - permanently. Measured: the rect predicate was correct
         (past = true at the document bottom) while the sync function was called
         exactly 0 times, so the bar never hid. Unique name = no collision. */
      var stickyTicking = false;

      /* The end condition is computed from the RECT, not from an
         IntersectionObserver entry. IO fires only when the intersection ratio
         crosses a threshold, and this band goes from "below the viewport,
         ratio 0" to "above the viewport, ratio 0" - the ratio never changes, so
         on any non-gradual scroll (anchor jump, End key, restored scroll
         position) NO callback ever fires. Measured with an instant jump to the
         document bottom: the band sat at top:-510 and the observer stayed
         silent, leaving the bar over the footer. Same class of trap as
         CLAUDE.md §10.32 - never ask an IO about something whose ratio does
         not move. */
      var pastEnd = function () {
        if (!stickyEnd) return false;
        return stickyEnd.getBoundingClientRect().top < window.innerHeight;
      };
      /* Single writer for the bar's visible state, mirroring the Corporate
         page's applySticky(). Both the class on the bar and the class on
         <body> are set here so they can never diverge. The body class used to
         lift the sitewide FAB clear of the bar; the FAB is gone (2026-08-28,
         WhatsApp sweep) but the class stays as the hook for any future
         floating widget - it has to live on <body> because the footer and the
         bar render in different .shopify-section wrappers with no common
         styleable ancestor. */
      var syncSticky = function () {
        var on = !heroIn && !pastEnd();
        stickyCta.classList.toggle('is-visible', on);
        document.body.classList.toggle('kmh-sticky-on', on);
      };

      if ('IntersectionObserver' in window) {
        // The hero DOES cross a threshold, so an observer is right for it.
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) { heroIn = entry.isIntersecting; });
          syncSticky();
        }, { threshold: 0 }).observe(heroCta);
      }

      /* rAF-throttled and passive: this only toggles a class (the bar animates
         via transform, composited), and it reads a DIFFERENT element than it
         writes, so there is no read-after-write layout thrash (§10.56). */
      window.addEventListener('scroll', function () {
        if (stickyTicking) return;
        stickyTicking = true;
        window.requestAnimationFrame(function () { stickyTicking = false; syncSticky(); });
      }, { passive: true });

      /* syncSticky() on resize too: an orientation change or a window resize
         that crosses the 768px band never fires a scroll event, so without
         this the bar's layout (mobile vs desktop) could go stale relative to
         its visibility state. */
      window.addEventListener('resize', syncSticky, { passive: true });
      syncSticky();
    }

    /* ── Self-serve hours (AUTOMATED): tag filters + paged grid ───────────
       v3, 2026-08-17. The grid is collection-driven and each card is now a
       link to that hour's own product page (data-card-link, handled by the
       globally-loaded kitsters-card-click.js). Ordering - quantity, live
       totals, escalation and the Buy Now -> Magic Checkout hand-off - moved
       to assets/kitsters-magic-hour-product.js with the panel it belonged to;
       what remains here is purely which cards are visible. */
    var hoursSec = root.querySelector('[data-hours]');
    if (hoursSec) {
      /* ── Tag filters + paged grid, ONE visibility owner ─────────────────
         v3, 2026-08-13 (owner: "show 6 card first and give a button to show
         more... for mobile 3 card will show 1st"). A card can now be hidden
         for two independent reasons - it does not match the active filter, or
         it is past the current page - so both are resolved in a single
         `render()` and nothing else in this file writes `card.hidden`. Two
         writers would race the moment a filter changed mid-page.

         The step comes from `--kmh-hours-step` on the grid (3 below 640px, 6
         at/above it), so the breakpoint lives in the stylesheet next to the
         column-count rule it is derived from, and JS cannot drift from it
         (§10.56b). Reading it back through getComputedStyle also means a
         resize across the breakpoint needs no matchMedia of its own. */
      var hoursGrid = hoursSec.querySelector('[data-hours-grid]');
      var filterRow = hoursSec.querySelector('[data-hours-filters]');
      var moreWrap = hoursSec.querySelector('[data-hours-more-wrap]');
      var moreBtn = hoursSec.querySelector('[data-hours-more]');
      var moreCount = hoursSec.querySelector('[data-hours-count]');
      var hourCards = Array.prototype.slice.call(hoursSec.querySelectorAll('[data-hour]'));

      if (hoursGrid && hourCards.length) {
        var activeFilter = 'all';
        var shownCount = 0;

        var pageStep = function () {
          var raw = getComputedStyle(hoursGrid).getPropertyValue('--kmh-hours-step');
          var n = parseInt(raw, 10);
          return n > 0 ? n : 3;
        };
        var isMatch = function (card) {
          if (activeFilter === 'all') return true;
          return (card.getAttribute('data-tags') || '').split('|').indexOf(activeFilter) > -1;
        };

        /* focusFirstNew: index the reveal should land on after "Show more".
           Passing -1 means "don't move focus" (filter change, resize). */
        var render = function (focusFrom) {
          var matched = hourCards.filter(isMatch);
          var step = pageStep();
          if (shownCount < step) shownCount = step;
          if (shownCount > matched.length) shownCount = matched.length;

          var visible = matched.slice(0, shownCount);
          hourCards.forEach(function (card) {
            var matches = isMatch(card);
            var show = matches && visible.indexOf(card) > -1;
            card.classList.toggle('is-filtered-out', !matches);
            /* class + hidden together: the reveal animation sets opacity/
               transform on these cards, and [hidden] alone loses to any
               display-setting class (§10.51) - the CSS ships the guard. */
            card.hidden = !show;
            /* A card the shopper has not asked for yet must not be a tab stop
               on the way to the button, and must not be read out. */
            card.removeAttribute('tabindex');
          });

          /* The button's label is static in the Liquid ("Show more") - JS owns
             only whether the control exists. The remaining count lives in the
             live region below it, so there is nothing here to keep in sync. */
          var left = matched.length - shownCount;
          if (moreWrap) moreWrap.hidden = left <= 0;
          if (moreCount) {
            moreCount.textContent = matched.length
              ? 'Showing ' + shownCount + ' of ' + matched.length + ' kits'
              : '';
          }

          if (focusFrom > -1 && visible[focusFrom]) {
            var target = visible[focusFrom];
            target.setAttribute('tabindex', '-1');
            try { target.focus({ preventScroll: true }); } catch (err) { target.focus(); }
          }
        };

        if (moreBtn) {
          moreBtn.addEventListener('click', function () {
            var firstNew = shownCount;
            shownCount += pageStep();
            render(firstNew);
          });
        }

        if (filterRow) {
          filterRow.addEventListener('click', function (e) {
            var chip = e.target.closest('.kmh__hours-chip');
            if (!chip) return;
            filterRow.querySelectorAll('.kmh__hours-chip').forEach(function (c) {
              var on = c === chip;
              c.classList.toggle('is-on', on);
              c.setAttribute('aria-pressed', on ? 'true' : 'false');
            });
            activeFilter = chip.getAttribute('data-filter');
            /* A new filter is a new list, so it starts at page one - otherwise
               picking a 4-product tag after paging to 24 shows everything and
               the control vanishes with no explanation. */
            shownCount = 0;
            render(-1);
          });
        }

        /* Crossing 640px changes the step, so re-clamp to a whole number of
           pages of the NEW step - never shrinking below what is already on
           screen, which would yank cards away mid-scroll. */
        var lastStep = pageStep();
        window.addEventListener('resize', function () {
          var step = pageStep();
          if (step === lastStep) return;
          lastStep = step;
          shownCount = Math.ceil(shownCount / step) * step;
          render(-1);
        });

        /* Hand the CSS pre-clamp over to JS only once state is live, so the
           first paint is the server-rendered page and there is no flash. */
        hoursGrid.classList.add('is-paged');
        render(-1);
      }

      /* The per-card Buy Now block that used to sit here was REMOVED with the
         inline buy panel (2026-08-17). Clicking a card now opens that hour's
         own product page, which owns ordering end to end - including the
         identical Magic Checkout contract (clear cart -> add qty x variant ->
         click a real name="checkout" submit) and the same overlay-dismissal
         restore machinery. See assets/kitsters-magic-hour-product.js.
         Deleted with it: the `inr` / `waHref` / `hoursQuoteAt` helpers above,
         which had no other caller once `calc()` went. The tag filters and the
         show-more paging above are untouched. */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
